import type React from 'react'
import { useEffect, useState } from 'react'
import {
  AddEPLITrainerDevelopmentPlan,
  UpdateEPLITrainerDevelopmentPlan
} from '../../../services/EPLITrainerDevelopmentPlanService'
import { GetTrainings } from '../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { type EPLITrainerDevelopmentPlan } from '../../../models/EPLITrainerDevelopmentPlan'
import { Modal } from '../../customComponents/Modal'
import {
  Alert,
  Box,
  CircularProgress,
  FormControl, FormControlLabel, FormLabel, InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent, Stack, TextField,
  Typography
} from '@mui/material'
import { EventDisplay } from '../../customComponents/EventDisplay'
import { ActivityEditSession } from './priorityActivities/ActivityEditSession'
import { PriorityOneActivityName } from '../../../models/PriorityOneActivityName'
import { TrainingType, type Training } from '../../../models/Training'
import { EPLITrainerStatus } from '../../../models/EPLITrainerStatus'
import { usePlanOrganizationOrgs } from '../../../hooks/use-planOrganizationOrgs'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BudgetThresholdExceededModal } from './BudgetExceededModal'
import { useConfirmState } from '../../../hooks/use-budgetThresholdExceeded'
import type { FieldError } from '../../../hooks/use-fetch'

interface EPLIForm {
  epliTrainingId: number
  numberOfAttendees: number
  budgetRequest?: number
}

interface AttendeeForm {
  id?: number
  fullName: string
  isAttendeeFromDistrict: boolean | null
  isdId: number | null
  districtId?: number | null
  status: EPLITrainerStatus | null
  completionStatus?: string | null
}

interface Props {
  annualPlanId: number
  orgId: number
  priorityId: number
  epliTrainerDevelopmentPlan?: EPLITrainerDevelopmentPlan
  onClose: () => void
  onSave: () => void
  implementationPhase?: boolean
  planOrgId: number
  usedIds: number[]
  startOnAdditionalDetails?: boolean
}

export const EPLITrainerDevelopmentPlanModal = (props: Props): JSX.Element => {
  const isEditing = props.epliTrainerDevelopmentPlan != null
  const isImplementationPhase = props.implementationPhase ?? false

  const [formValues, setFormValues] = useState<EPLIForm>({
    epliTrainingId: props.epliTrainerDevelopmentPlan?.epliTraining.id ?? 0,
    numberOfAttendees: props.epliTrainerDevelopmentPlan?.numberOfAttendees ?? 0,
    budgetRequest: isImplementationPhase
      ? (props.epliTrainerDevelopmentPlan?.adjustedDollarAmount ?? props.epliTrainerDevelopmentPlan?.budgetRequest)
      : props.epliTrainerDevelopmentPlan?.budgetRequest
  })

  const [attendees, setAttendees] = useState<AttendeeForm[]>(
    props.epliTrainerDevelopmentPlan?.attendees?.map(a => ({
      id: a.id,
      fullName: a.fullName,
      isdId: a.isd.id,
      isAttendeeFromDistrict: a.district != null,
      districtId: a.district?.id,
      status: a.status,
      completionStatus: a.completionStatus ?? null
    })) ?? []
  )

  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const [attendeeErrors, setAttendeeErrors] = useState<FieldError[]>([])
  const [epliTrainingOptions, setEPLITrainingOptions] = useState<Training[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [modalPage, setModalPage] = useState<'edit' | 'details'>(props.startOnAdditionalDetails === true ? 'details' : 'edit')
  const [showCancelUpdatesModal, setShowCancelUpdatesModal] = useState<boolean>(false)
  const [saving, setSaving] = useState(false)

  const { confirmState, setConfirmState, overBudgetThreshold } = useConfirmState(
    formValues.budgetRequest,
    props.epliTrainerDevelopmentPlan?.adjustedDollarAmount
  )

  const { isLoading: isLoadingPlanOrgOrgs, planOrganizationOrgs } = usePlanOrganizationOrgs(props.planOrgId)
  const isd = planOrganizationOrgs?.isd
  const districts = planOrganizationOrgs?.districts

  useEffect(() => {
    const fetchEPLITrainingOptions = async (): Promise<void> => {
      setIsLoading(true)
      const trainings = await GetTrainings(TrainingType.EPLI)
      const filteredTrainings = trainings.filter(t => !props.usedIds.includes(t.id ?? -1) || (props.epliTrainerDevelopmentPlan != null && t.id === props.epliTrainerDevelopmentPlan.epliTraining.id))

      setEPLITrainingOptions(filteredTrainings)
      setIsLoading(false)
    }

    void fetchEPLITrainingOptions()
  }, [])

  useEffect(() => {
    if (confirmState === 'confirmed') {
      void handleSave()
    }
  }, [confirmState])

  const validateForm = (): boolean => {
    const errors: FieldError[] = []

    if (formValues.epliTrainingId == null || formValues.epliTrainingId <= 0) {
      errors.push({ fieldName: 'epliTrainingId', message: 'Please select a training' })
    }

    if (formValues.numberOfAttendees == null || formValues.numberOfAttendees <= 0) {
      errors.push({ fieldName: 'numberOfAttendees', message: 'Number of attendees must be greater than 0' })
    }

    if (formValues.budgetRequest == null || formValues.budgetRequest < 0) {
      errors.push({ fieldName: 'budgetRequest', message: 'Budget must be greater than or equal to 0' })
    }

    setFieldErrors(errors)
    if (errors.length > 0) {
      toast.error('Please fill out required fields.')
      errors.forEach((error) => {
        if (error.message !== '') {
          toast.error(error.message)
        }
      })
      return false
    }

    return true
  }

  const validateAttendees = (): boolean => {
    const errors: FieldError[] = []

    attendees.forEach((attendee, index) => {
      if (attendee.fullName.trim() === '') {
        errors.push({ fieldName: `attendees[${index}].fullName`, message: `Attendee ${index + 1}: Full name is required` })
      }

      if (attendee.isdId == null || attendee.isdId <= 0) {
        errors.push({ fieldName: `attendees[${index}].isdId`, message: `Attendee ${index + 1}: ISD is required` })
      }

      if (attendee.isAttendeeFromDistrict === true && (attendee.districtId == null || attendee.districtId <= 0)) {
        errors.push({ fieldName: `attendees[${index}].districtId`, message: `Attendee ${index + 1}: District is required` })
      }

      if (attendee.status == null || !Object.values(EPLITrainerStatus).includes(attendee.status)) {
        errors.push({ fieldName: `attendees[${index}].status`, message: `Attendee ${index + 1}: Status is required` })
      }
    })

    setAttendeeErrors(errors)
    if (errors.length > 0) {
      toast.error('Please fill out required additional detail fields')
      return false
    }

    return true
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target

    // Convert numeric fields to numbers
    let parsedValue: string | number | undefined = value
    if (name === 'numberOfAttendees' || name === 'budgetRequest') {
      parsedValue = value === '' ? undefined : Number(value)
    }

    setFormValues({ ...formValues, [name]: parsedValue })
    setFieldErrors(fieldErrors.filter(f => f.fieldName !== name))
  }

  const handleSubmit = async (): Promise<void> => {
    if (isImplementationPhase && modalPage === 'edit') {
      if (!validateForm()) return

      const newNumberOfAttendees = formValues.numberOfAttendees ?? 0

      let updatedAttendees: AttendeeForm[]

      if (newNumberOfAttendees > attendees.length) {
        const numberOfNewAttendees = newNumberOfAttendees - attendees.length
        const newAttendees: AttendeeForm[] = Array.from({ length: numberOfNewAttendees }, () => ({
          fullName: '',
          isdId: isd?.id ?? null,
          isAttendeeFromDistrict: null,
          districtId: null,
          status: null,
          completionStatus: null
        }))
        updatedAttendees = [...attendees, ...newAttendees]
      } else if (newNumberOfAttendees < attendees.length) {
        updatedAttendees = attendees.slice(0, newNumberOfAttendees)
      } else {
        updatedAttendees = attendees
      }

      setAttendees(updatedAttendees)
      setModalPage('details')
      return
    }

    // Handle save
    await handleSave()
  }

  const handleSave = async (): Promise<void> => {
    if (saving) return

    if (isImplementationPhase && modalPage === 'details') {
      if (!validateAttendees()) return
    } else {
      if (!validateForm()) return
    }

    if (isEditing && overBudgetThreshold && isImplementationPhase && confirmState !== 'confirmed') {
      setConfirmState('needsConfirmation')
      return
    }

    setSaving(true)

    const baseRequest = {
      priorityId: props.priorityId,
      epliTrainingId: formValues.epliTrainingId ?? 0,
      numberOfAttendees: formValues.numberOfAttendees ?? 0,
      budgetRequest: isImplementationPhase ? (props.epliTrainerDevelopmentPlan?.adjustedDollarAmount ?? 0) : (formValues.budgetRequest ?? 0),
      adjustedDollarAmount: isImplementationPhase ? (formValues.budgetRequest ?? 0) : undefined,
      isImplementationPhase,
      needsReview: overBudgetThreshold,
      ...(isImplementationPhase && modalPage === 'details' && {
        attendees: attendees.map(a => ({
          id: a.id,
          fullName: a.fullName,
          isdId: a.isdId ?? 0,
          isAttendeeFromDistrict: a.districtId != null,
          districtId: a.districtId,
          status: a.status ?? '' as any,
          completionStatus: a.completionStatus
        }))
      })
    }

    const { success, errors } = isEditing && props.epliTrainerDevelopmentPlan != null
      ? await UpdateEPLITrainerDevelopmentPlan({
        ...baseRequest,
        id: props.epliTrainerDevelopmentPlan.id,
        isUpdate: props.epliTrainerDevelopmentPlan.isUpdate ?? false,
        actualAmount: props.epliTrainerDevelopmentPlan.actualBudgetAmount
      })
      : await AddEPLITrainerDevelopmentPlan(baseRequest)

    if (!success) {
      errors.forEach(e => toast.error(e))
      setSaving(false)
      return
    }

    const planType = isImplementationPhase ? 'EPLI Trainer Plan' : 'Attendance Plan'
    toast.success(`${planType} ${isEditing ? 'Updated' : 'Saved'}`)
    props.onSave()
    setSaving(false)
  }

  const selectedTraining = epliTrainingOptions.find((training) => training?.id != null && training.id.toString() === formValues.epliTrainingId?.toString())

  const getTitle = (): string => {
    if (isImplementationPhase && isEditing) return 'Update EPLI Trainer Plan'
    if (isEditing) return 'Edit EPLI Trainer Plan'
    return 'Add EPLI Trainer Plan'
  }

  const handleClose = (): void => {
    if (isImplementationPhase && modalPage === 'details') {
      setShowCancelUpdatesModal(true)
    } else {
      props.onClose()
    }
  }

  const handleCancel = (): void => {
    if (isImplementationPhase && modalPage === 'details') {
      setModalPage('edit')
    } else {
      props.onClose()
    }
  }

  const getDataTestId = (): string => {
    if (isImplementationPhase) return 'updateEPLITrainerDevelopmentPlanModal'
    return isEditing ? 'editEPLITrainerDevelopmentPlanModal' : 'addEPLITrainerDevelopmentPlanModal'
  }

  const handleUpdateAttendeeField = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>): void => {
    const { name, value } = e.target

    const updatedAttendees = [...attendees]
    if (name === 'orgTypeRadio') {
      const isFromDistrict = value === 'Yes'
      updatedAttendees[index] = {
        ...updatedAttendees[index],
        isAttendeeFromDistrict: isFromDistrict,
        districtId: isFromDistrict ? updatedAttendees[index].districtId : null
      }
    } else {
      updatedAttendees[index] = { ...updatedAttendees[index], [name]: value }
    }
    setAttendees(updatedAttendees)
    setAttendeeErrors(attendeeErrors.filter(f => !f.fieldName.startsWith(`attendees[${index}]`)))
  }

  const handleConfirmCancelUpdates = async (): Promise<void> => {
    setShowCancelUpdatesModal(false)
    setSaving(true)

    if (props.epliTrainerDevelopmentPlan == null) {
      toast.error('EPLI Trainer Development Plan not found')
      setSaving(false)
      return
    }

    const request = {
      id: props.epliTrainerDevelopmentPlan.id,
      priorityId: props.priorityId,
      epliTrainingId: formValues.epliTrainingId ?? 0,
      numberOfAttendees: formValues.numberOfAttendees ?? 0,
      budgetRequest: props.epliTrainerDevelopmentPlan.budgetRequest,
      adjustedDollarAmount: isImplementationPhase ? (formValues.budgetRequest ?? 0) : undefined,
      isImplementationPhase,
      isUpdate: props.epliTrainerDevelopmentPlan.isUpdate ?? false,
      actualAmount: props.epliTrainerDevelopmentPlan.actualBudgetAmount,
      attendees: props.epliTrainerDevelopmentPlan.attendees?.map(a => ({
        id: a.id,
        fullName: a.fullName,
        isdId: a.isd.id,
        isAttendeeFromDistrict: a.district != null,
        districtId: a.district?.id,
        status: a.status,
        completionStatus: a.completionStatus ?? null
      })) ?? []
    }

    const { success, errors } = await UpdateEPLITrainerDevelopmentPlan(request)
    if (!success) {
      errors.forEach(e => toast.error(e))
      setSaving(false)
      return
    }

    const planType = isImplementationPhase ? 'EPLI Trainer Plan' : 'Attendance Plan'
    toast.success(`${planType} Partially Updated`)
    props.onSave()
    setSaving(false)
  }

  if (showCancelUpdatesModal) {
    return (
      <Modal
        open={true}
        title='Cancel Updates'
        cancelButtonText='Go Back'
        confirmButtonText='Confirm Cancel'
        data-testid='confirmCancelUpdatesModal'
        bodyContent={<Stack gap='1rem' m='24px 0'>
          <Typography variant='body1' fontWeight={600}>Are you sure you want to cancel?</Typography>
          <Typography variant='body1'>If you proceed your changes in the Additional Details section will not be saved.</Typography>
        </Stack>}
        onClose={() => { setShowCancelUpdatesModal(false) }}
        onConfirm={handleConfirmCancelUpdates}
        maxWidth='xs'
      />
    )
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose()
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
        }}
    />
    : <Modal
        title={getTitle()}
        open={true}
        data-testid={getDataTestId()}
        width='400px'
        confirmButtonText={isImplementationPhase && modalPage === 'edit' ? 'Save & Continue' : 'Save'}
        cancelButtonText={isImplementationPhase && modalPage === 'details' ? 'Go Back' : 'Cancel'}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {modalPage === 'edit' && (
              isLoading
                ? <CircularProgress />
                : <>
                  {!isImplementationPhase && (
                    <Typography
                      variant='body1'
                      sx={{
                        backgroundColor: 'var(--blue-50)',
                        border: '1px solid var(--blue-300)',
                        borderRadius: '8px',
                        padding: '10px 32px'
                      }}
                    >
                      You will have the opportunity to further define specific participant information during implementation.
                    </Typography>
                  )}
                  {
                    isImplementationPhase && !isEditing &&
                      <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                        backgroundColor: 'var(--blue-500)',
                        color: 'white',
                        fill: 'white'
                      }} severity="info"
                      >The addition of this item will prompt a review from the START team.
                      </Alert>
                  }
                  <Typography variant='h5'>EPLI Trainings</Typography>
                  <Typography variant='body1'>Please select the EPLI trainings that will be attended, enter the number of attendees along with the budget request.</Typography>
                  <FormControl
                    fullWidth
                    required
                    error={fieldErrors.some(e => e.fieldName === 'epliTrainingId')}
                  >
                    <InputLabel id='training-label'>Training Name</InputLabel>
                    <Select
                      labelId='training-label'
                      id='training'
                      value={(epliTrainingOptions.length > 0 ? formValues.epliTrainingId : '') ?? ''}
                      label='Training Name'
                      name='epliTrainingId'
                      data-testid='training'
                      onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.epliTrainingId>) => void}
                    >
                      {epliTrainingOptions.length === 0 && <Typography textAlign='center'>No trainings available.</Typography>}
                      {epliTrainingOptions.map((training) => (
                        <MenuItem key={training.id} value={training.id} data-testid='trainingOption'>{training.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedTraining != null &&
                    <EventDisplay
                      eventBoxStyle={{ width: '2.25em', height: '3.375em', my: '.5em' }}
                      event={{ title: selectedTraining.title, date: selectedTraining.meetingDate }}
                      description={selectedTraining.description}
                      seriesLetter='E'
                    />
                }

                  <TextField
                    name='numberOfAttendees'
                    label='Number Attending'
                    value={formValues.numberOfAttendees ?? ''}
                    data-testid='numberOfAttendees'
                    onChange={handleFieldUpdate}
                    variant='filled'
                    required
                    error={fieldErrors.some(e => e.fieldName === 'numberOfAttendees')}
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Typography variant='body1'>Does this require additional funding to complete?</Typography>
                    <TextField
                      id='budget-request'
                      sx={{ mt: '.75em', width: '250px' }}
                      variant='filled'
                      onChange={handleFieldUpdate}
                      label={'Budget Request'}
                      data-testid='budgetRequest'
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      name='budgetRequest'
                      required
                      error={fieldErrors.some(e => e.fieldName === 'budgetRequest')}
                      value={formValues.budgetRequest ?? ''}
                    />
                  </Box>

                  {props.epliTrainerDevelopmentPlan != null &&
                    <ActivityEditSession
                      activityId={props.epliTrainerDevelopmentPlan.id}
                      activityName={PriorityOneActivityName.EpliPlan}
                      onClose={props.onClose}
                    />
                }
                </>
            )}

            {modalPage === 'details' && (
              <Stack gap='1rem' py='.5rem'>
                <Typography variant='h4'>Additional Details</Typography>

                {attendees.length === 0 && (
                  <Typography variant='body1'>No attendees have been added to this plan.</Typography>
                )}
                {attendees.map((attendee, index) => (
                  <Stack key={index} gap='1rem'>
                    <Typography variant='body1' fontWeight={600}>Attendee ({index + 1})</Typography>
                    <TextField
                      id={`attendeeFullName-${index}`}
                      name='fullName'
                      label='Attendee Full Name'
                      value={attendee.fullName}
                      data-testid={`attendeeFullName-${index}`}
                      onChange={(e) => { handleUpdateAttendeeField(index, e) }}
                      variant='filled'
                      required
                      error={attendeeErrors.some(e => e.fieldName === `attendees[${index}].fullName`)}
                    />

                    <FormControl
                      sx={{ alignContent: 'center', width: '100%' }}
                      data-testid='formControlOrgRadio'
                      required
                      error={attendeeErrors.some(e => e.fieldName === `attendees[${index}].isAttendeeFromDistrict`)}
                    >
                      <FormLabel>Is attendee part of a District?</FormLabel>
                      <RadioGroup
                        row={true}
                        aria-labelledby="scope-radio-buttons-group"
                        name="orgTypeRadio"
                        value={attendee.isAttendeeFromDistrict === true ? 'Yes' : attendee.isAttendeeFromDistrict === false ? 'No' : ''}
                        onChange={(e) => { handleUpdateAttendeeField(index, e) }}
                      >
                        <FormControlLabel data-testid={`${index}districtRadio`} value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel data-testid={`${index}isdRadio`} value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>

                    {
                      attendee.isAttendeeFromDistrict !== true
                        ? null
                        : isLoadingPlanOrgOrgs
                          ? <CircularProgress sx={{ alignSelf: 'center' }} />
                          : <FormControl
                              fullWidth
                              required
                              error={attendeeErrors.some(e => e.fieldName === `attendees[${index}].districtId`)}
                          >
                            <InputLabel id="districtLabel">District</InputLabel>
                            <Select
                              labelId="districtLabel"
                              id={`district-${index}`}
                              value={districts?.some(d => d.id === attendee.districtId) === true ? attendee.districtId : ''}
                              label="District"
                              name='districtId'
                              data-testid={`attendeeDistrict-${index}`}
                              onChange={(e) => { handleUpdateAttendeeField(index, e) }}
                            >
                              {districts?.length === 0 && <Typography variant='body1' textAlign={'center'}>No district options</Typography>}
                              {(districts?.length ?? 0) > 0 && districts?.map((district) => (
                                <MenuItem key={district.id} value={district.id} data-testid='attendeeDistrictOption'>{district.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                    }

                    <FormControl
                      variant='filled'
                      required
                      error={attendeeErrors.some(e => e.fieldName === `attendees[${index}].status`)}
                    >
                      <FormLabel id={`statusLabel-${index}`}>What is the attendee's current EPLI Trainer status?</FormLabel>
                      <RadioGroup
                        aria-labelledby={`statusLabel-${index}`}
                        value={attendee.status ?? ''}
                        id={`status-${index}`}
                        name='status'
                        data-testid={`attendeeStatus-${index}`}
                        onChange={(e) => { handleUpdateAttendeeField(index, e) }}
                      >
                        {Object.entries(EPLITrainerStatus).map(([key, status]) => (
                          <FormControlLabel
                            key={key}
                            data-testid={`attendeeStatusOption-${index}-${key}`}
                            value={status}
                            control={<Radio />}
                            label={status}
                            sx={{
                              '& .MuiSvgIcon-root': {
                                fontSize: 14
                              }
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        }
    />
  )
}
